import _ from 'lodash'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
} from '@material-ui/core'
import AddBtn from 'components/AddBtn'
import EditBtn from 'components/EditBtn'
import DeleteBtn from 'components/DeleteBtn'
import { isBlank } from 'common/usual'
import { translationJsonpathListPromise, translationJsonpathDeletePromise } from 'api/TranslationJsonpath'
import { useLocation } from 'react-router-dom'

// The live localization config. `name` is the method-route the response path looks up
// ('get-/api/v1/social-feed/posts'), `jsonpathStr` the JSON array of paths applied to that route's
// body. Not translation_jsonpath_response, which is mq's capture table and is never hand-edited.
export default function TranslationJsonpathList (props) {
  const ref = useRef()
  const location = useLocation()
  const [data, setData] = useState({})
  const [params, setParams] = useState({})

  const load = useCallback(query => translationJsonpathListPromise(query).then(resp => setData(resp || {})), [])

  useEffect(() => {
    let cancelled = false
    const query = Object.fromEntries(new URLSearchParams(location.search).entries())

    setParams({ page: 1, ...query })
    translationJsonpathListPromise(query).then(resp => { if (!cancelled) setData(resp || {}) })

    return () => { cancelled = true }
  }, [location.search])

  const handleParamsChange = useCallback((payload, delay = 0) => {
    if (ref.current) clearTimeout(ref.current)

    const query = Object.fromEntries(new URLSearchParams(location.search))
    const next = _.pickBy({ ...query, page: 1, ...payload }, _.identity)
    setParams(next)

    ref.current = setTimeout(() => props.history.push(`?${new URLSearchParams(next)}`), delay)
  }, [location.search, props.history])

  function handlePageChange (event, page) {
    handleParamsChange({ page: page + 1 })
  }

  function handleDelete (row) {
    if (!window.confirm(`Stop localizing ${row.name}? Responses from that route are served untranslated from the next request.`)) return

    translationJsonpathDeletePromise(row.id)
      .then(() => load(Object.fromEntries(new URLSearchParams(location.search).entries())))
  }

  return (
    <div className={'root'}>
      <div className={'content'}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              placeholder='Route, e.g. get-/api/v1/social-feed/posts'
              value={params.keyword || ''}
              className='background-color'
              onChange={event => handleParamsChange({ keyword: event.target.value }, 500)}
            />
          </Grid>
          <Grid item xs={8} className='flex-right'>
            <AddBtn color='primary' variant='contained' onClick={() => props.history.push('/translation-jsonpaths/add')}>
              Add
            </AddBtn>
          </Grid>
        </Grid>

        <br />

        <Card>
          <CardHeader
            title='Response localization paths'
            subheader='Every string a path here matches is hashed and, on a miss, registered as a new en_US source string bound for Lokalise -- forever. Point a path at a template, never at a rendered sentence, a count or member-written text.'
          />
          <CardContent className={'table-content'}>
            <PerfectScrollbar>
              <div className={'inner'}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Route</TableCell>
                      <TableCell>Paths</TableCell>
                      <TableCell>Avg Rating</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.rows && data.rows.map(row => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.name}</TableCell>
                        <TableCell style={{ maxWidth: 520, wordBreak: 'break-all' }}>{row.jsonpathStr}</TableCell>
                        <TableCell>{isBlank(row.avgRating) ? '-' : row.avgRating}</TableCell>
                        <TableCell className='flex-left'>
                          <EditBtn history={props.history} path={`/translation-jsonpaths/edit/${row.id}`} />
                          <DeleteBtn onClick={() => handleDelete(row)} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>
          </CardContent>

          <CardActions className='flex-right'>
            <TablePagination
              component='div'
              rowsPerPage={10}
              page={(params.page || 1) - 1}
              count={data.count || 0}
              rowsPerPageOptions={[10]}
              onPageChange={handlePageChange}
            />
          </CardActions>
        </Card>
      </div>
    </div>
  )
}
